// Utility functions for user management

/**
 * Get current user data from localStorage
 * @returns {Object|null} User object or null if not found
 */
export const getCurrentUser = () => {
  try {
    const userString = localStorage.getItem("user");
    return userString ? JSON.parse(userString) : null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

/**
 * Get current user's department with proper error handling
 * IMPORTANT: This function now throws errors instead of returning null
 * This prevents accidental fallback to UEU department
 * 
 * @returns {string} User's department (uppercase, trimmed)
 * @throws {Error} If user is not logged in or department is missing
 */
export const getCurrentUserDepartment = () => {
  const user = getCurrentUser();

  if (!user) {
    console.error("getCurrentUserDepartment: No user data found in localStorage");
    throw new Error("Session expired. Please login again.");
  }

  if (!user.bahagian) {
    console.error("getCurrentUserDepartment: User object exists but has no bahagian field:", user);
    throw new Error("User department not found. Please contact administrator.");
  }

  if (typeof user.bahagian !== "string" || user.bahagian.trim() === "") {
    console.error("getCurrentUserDepartment: bahagian field is empty or invalid:", user.bahagian);
    throw new Error("Invalid user department. Please login again.");
  }

  console.log("getCurrentUserDepartment: Successfully retrieved department:", user.bahagian);
  return user.bahagian.trim().toUpperCase();
};

/**
 * Get current user's department with fallback (DEPRECATED - use getCurrentUserDepartment)
 * This function is for backward compatibility only - returns null instead of throwing
 * Use getCurrentUserDepartment() for new code
 * 
 * @returns {string|null} User's department or null if not found/invalid
 * @deprecated Use getCurrentUserDepartment() instead
 */
export const getCurrentUserDepartmentSafe = () => {
  try {
    return getCurrentUserDepartment();
  } catch (error) {
    console.warn("getCurrentUserDepartmentSafe: Caught error, returning null:", error.message);
    return null;
  }
};

/**
 * Get user department or throw with user-friendly message
 * This is a convenience wrapper for API calls
 * 
 * @param {string} context - Context for error message (e.g., "fetching accounts")
 * @returns {string} User's department
 * @throws {Error} With context-specific message
 */
export const requireUserDepartment = (context = "performing this action") => {
  try {
    return getCurrentUserDepartment();
  } catch (error) {
    throw new Error(`Unable to ${context}: ${error.message}`);
  }
};

/**
 * Check if current user belongs to a specific department
 * @param {string} department - Department to check
 * @returns {boolean} True if user belongs to the department
 */
export const isUserInDepartment = (department) => {
  const userDepartment = getCurrentUserDepartment();
  if (!userDepartment || !department) return false;
  return userDepartment.toLowerCase() === department.toLowerCase();
};

/**
 * Get user display name (prioritizes bahagian over id_pengguna)
 * @returns {string} Display name for the user
 */
export const getUserDisplayName = () => {
  const user = getCurrentUser();
  if (!user) return "Guest";

  return user.bahagian || user.id_pengguna || "Unknown User";
};
